// Direct messages: private conversations between 2–10 people of one server. They live in the server's room as
// channels with kind "dm" (see Channel in protocol.ts); only their members ever hear about them.
import type { Channel } from "./protocol.ts";

/** People in one DM, you included. */
export const MAX_DM_MEMBERS = 10;

export const isDm = (c: Pick<Channel, "kind"> | undefined | null): boolean => c?.kind === "dm";

/**
 * Whether a DM I closed stays out of my sidebar (#131): until a message comes after the one it ended on when I closed
 * it (from anyone, on any device), and never while it's the conversation open on screen. `closedSeq`: its lastSeq when
 * closed, or undefined when it isn't closed.
 */
export const dmHidden = (lastSeq: number, closedSeq: number | undefined, open: boolean): boolean =>
  closedSeq !== undefined && lastSeq <= closedSeq && !open;

/** The other people in a DM (everyone but me), in the order the room keeps them. */
export const dmOthers = (c: Pick<Channel, "members">, me: string | null): string[] => (c.members ?? []).filter((id) => id !== me);

/** What a DM is called in lists and headers: the other person's name, or first names for a group ("Maya, Sam"). */
export function dmTitle(c: Pick<Channel, "members">, me: string | null, names: Map<string, string>): string {
  const others = dmOthers(c, me);
  if (!others.length) return "Only you";
  const name = (id: string) => names.get(id) ?? "Someone";
  if (others.length === 1) return name(others[0]!);
  return others.map((id) => name(id).split(/\s+/)[0]).join(", ");
}
